/**
 * Distinguishes hosted prod vs hosted/local non-prod.
 *
 * Important: ECS builds use `NODE_ENV=production` for both dev and prod images.
 * Do not use NODE_ENV for SEO/analytics gating — use `NEXT_PUBLIC_DEPLOY_ENV`.
 *
 * Set `NEXT_PUBLIC_DEPLOY_ENV=production` only on the production web build.
 * Dev / local leave it unset or `development` so crawlers and trackers stay off.
 *
 * Use dot access (`process.env.NEXT_PUBLIC_…`) so Next.js inlines the value into
 * client bundles; bracket access is not reliably replaced.
 */
export type PublicDeployEnv = "production" | "development";

export function getPublicDeployEnv(): PublicDeployEnv {
  const raw = (process.env.NEXT_PUBLIC_DEPLOY_ENV ?? "").trim().toLowerCase();
  if (raw === "production" || raw === "prod") {
    return "production";
  }
  return "development";
}

/** GA4 / Clarity / Plausible / indexable SEO — production public site only. */
export function isProdPublicTrackingEnabled(): boolean {
  return getPublicDeployEnv() === "production";
}

/**
 * Server-side production check for SEO decisions that are resolved during the
 * build (static route handlers, prerendered metadata).
 *
 * `NEXT_PUBLIC_DEPLOY_ENV` is inlined by Next at build time. The prod image is
 * built with placeholder tokens (`production`) and only substitutes
 * strings inside `.next` at container start, so anything that *branches* on the
 * public value during the build bakes the non-production answer permanently —
 * which is how production shipped `Disallow: /` and sitewide `noindex`.
 *
 * `WEB_PUBLIC_DEPLOY_ENV` / `DEPLOY_ENV` are plain server env vars on the ECS
 * task, are never inlined, and are readable at request time. Use them for any
 * server-side indexability decision.
 */
export function isServerProdPublic(env: NodeJS.ProcessEnv = process.env): boolean {
  const web = (env["WEB_PUBLIC_DEPLOY_ENV"] ?? "").trim().toLowerCase();
  if (web) return web === "production" || web === "prod";
  const deploy = (env["DEPLOY_ENV"] ?? "").trim().toLowerCase();
  if (deploy) return deploy === "production" || deploy === "prod";
  // No server signal (local dev, CI): fall back to the inlined public value.
  return isProdPublicTrackingEnabled();
}
